"use client";

import React from "react";
import type { TabConfig, TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { CompanyEntity } from "@/lib/types/company";

// Import company detail config
import { getEntityDefinition } from "@/lib/entity-registry";

// Import company detail tab components
import { CompanyDetailTabComponents } from "@/features/companies/components/CompanyDetailTabComponents";
import { ContactMethodActions } from "@/components/shared/ContactMethodActions/ContactMethodActions";

// ============================================================================
// COMPANY DETAIL MODAL TABS HOOK
// ============================================================================

export interface UseCompanyDetailModalTabsReturn {
  tabs: TabConfig<CompanyEntity>[];
  renderPrimaryArea: (activeTab: string, company: CompanyEntity) => React.ReactNode;
  renderInfoPanel: (company: CompanyEntity) => React.ReactNode;
}

export function useCompanyDetailModalTabs(): UseCompanyDetailModalTabsReturn {
  // ✅ CRITICAL: Pull ALL tabs from the detail config (just like contacts does)
  // This ensures all tabs from the detail page are available in the modal:
  // - details, contacts, tasks, tickets, time-entries, recurring-transactions,
  //   timeline, drive, documentation, quickbooks
  const tabs = getEntityDefinition("companies").detailConfig.tabs as unknown as TabConfig<CompanyEntity>[];

  // Render primary area based on active tab
  const renderPrimaryArea = (activeTab: string, company: CompanyEntity): React.ReactNode => {
    // Get the tab config for this tab
    const tabConfig = tabs.find((t) => t.id === activeTab) || tabs[0];

    // Minimal props for TabContentProps - components will use context for hooks/callbacks
    const tabProps = {
      item: company,
      tab: tabConfig,
    };

    // Resolve the tab's configured custom component from the component map
    // (mirrors the full-page CustomTabContent path). Generic resolution — NOT a
    // hand-maintained switch — so a tab declared in the detail config can never
    // silently fall through to the default and render the wrong panel. That
    // drift is exactly what hid the "Support Link" tab (it was in the config +
    // the component map but had no switch case → rendered Company Details).
    const componentMap = CompanyDetailTabComponents as Record<
      string,
      React.ComponentType<TabContentProps<CompanyEntity>>
    >;
    const componentName =
      typeof tabConfig?.content?.customComponent === "string"
        ? tabConfig.content.customComponent
        : undefined;
    const ResolvedComponent =
      (componentName ? componentMap[componentName] : undefined) ??
      CompanyDetailTabComponents.CompanyDetailsDisplay;

    return <ResolvedComponent {...tabProps} />;
  };

  // Render info panel (stable across tabs)
  const renderInfoPanel = (company: CompanyEntity): React.ReactNode => {
    return (
      <div className="space-y-4">
        <div className="border rounded-lg p-4 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Info</h3>
          <div className="space-y-3 text-sm">
            {company.description && typeof company.description === "string" && company.description.trim() && (
              <div>
                <span className="text-gray-600">Description:</span>
                <p className="text-gray-900 line-clamp-4 whitespace-pre-wrap">{company.description.trim()}</p>
              </div>
            )}
            {company.status && (
              <div>
                <span className="text-gray-600">Status:</span>
                <p className="text-gray-900 font-medium capitalize">{company.status}</p>
              </div>
            )}
            {company.industry && (
              <div>
                <span className="text-gray-600">Industry:</span>
                <p className="text-gray-900">{company.industry}</p>
              </div>
            )}
            {company.domain && (
              <div>
                <span className="text-gray-600">Domain:</span>
                <p className="text-gray-900">{company.domain}</p>
              </div>
            )}
            {Array.isArray(company.tags) && company.tags.length > 0 && (
              <div>
                <span className="text-gray-600">Tags:</span>
                <p className="text-gray-900">{company.tags.filter(Boolean).join(", ")}</p>
              </div>
            )}
            {company.website && (
              <div>
                <span className="text-gray-600">Website:</span>
                <a 
                  href={company.website.startsWith("http") ? company.website : `https://${company.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline block truncate"
                >
                  {company.website}
                </a>
              </div>
            )}
            {company.organization_phone && (
              <div>
                <span className="text-gray-600">Phone:</span>
                <ContactMethodActions
                  kind="phone"
                  value={company.organization_phone}
                  entityContext={{
                    item: company as unknown as Record<string, unknown>,
                    entityType: "companies",
                  }}
                  className="block"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return {
    tabs,
    renderPrimaryArea,
    renderInfoPanel,
  };
}
